import React, { FC } from 'react';
import { useLocation } from 'react-router';
import { DetailEpisode, SeriesDetails } from 'types/Asset';
import { useBookmarks } from 'providers/useBookmarks/BookmarksContext';
import { useApp } from 'providers/useApp/AppContext';
import { ContentMarker } from 'style/styled-components/reusable.css';
import icons from 'style';
import { ImdbIcon, LabelMetaData } from 'components/Pages/PageDetailCommon/PageDetailCommon.css';
import { getVodPlayerURL, isHuubOrKidsPath } from 'utils/fnUrl';
import { buildEpisodeTitle } from 'utils/fnData';
import { CTAButton } from 'components/Buttons/CTAButton/CTAButton';
import { TrailerButton, WorldButton } from 'components/Pages/PageDetailCommon/PageDetailCommon';
import { BookmarkSectionType } from 'types/Bookmarks';
import { getVodContentMarker } from 'utils/fnContentMarker';
import { PageDetailHeader } from 'components/Pages/PageDetailHeader/PageDetailHeader';

const VODSeriesDetailsHeader: FC<{
    series: SeriesDetails<DetailEpisode>;
    episode?: DetailEpisode;
    overlayActive: boolean;
    setOverlayActive: (value: boolean) => void;
}> = ({ series, episode, overlayActive, setOverlayActive }) => {
    const { bookmarksInitialized } = useBookmarks();
    const { isMobile } = useApp();
    const { pathname } = useLocation();

    let watchPath = '';
    const metaData = [];
    const buttons = [];
    const mobileButtons = [];
    const iconList = [];

    if (!series) {
        return null;
    }

    if (episode) {
        const { release_year, production_countries, genres } = episode;
        const imdbRating = episode?.imdbRating;

        if (imdbRating && imdbRating > 0) {
            iconList.push(
                <LabelMetaData key={'imdb'} style={{ marginRight: '12px' }}>
                    <ImdbIcon svg={icons.imdbIcon} /> {imdbRating}
                </LabelMetaData>
            );
        }

        if (release_year && production_countries) {
            metaData.push(
                <LabelMetaData data-test-id={'tv-detailscreen-releaseyear'} key={'release-year'}>{`${production_countries
                    .slice(0, 2)
                    .join(', ')} ${release_year}`}</LabelMetaData>
            );
        }

        if (genres && genres.length && !isHuubOrKidsPath(pathname)) {
            metaData.push(<LabelMetaData key={'genres'}>{genres.slice(0, isMobile() ? 1 : 2).join(', ')}</LabelMetaData>);
        }

        watchPath = getVodPlayerURL(episode?.id);
        const ctaButton = <CTAButton asset={episode} key={'cta'} path={watchPath} screenName={'VODSeriesDetails'} />;
        buttons.push(ctaButton);
        mobileButtons.push(ctaButton);

        const trailerButton = <TrailerButton key={'trailer'} asset={episode} screenName={'VODSeriesDetails'} />;
        if (trailerButton) {
            buttons.push(trailerButton);
            mobileButtons.push(trailerButton);
        }

        if (bookmarksInitialized) {
            const myWorldButton = (
                <WorldButton
                    key={'favorite'}
                    worldId={series.id}
                    type={BookmarkSectionType.VOD_SERIES}
                    screenName={'VODSeriesDetails'}
                    assetName={series.title}
                />
            );
            buttons.push(myWorldButton);
            mobileButtons.push(myWorldButton);
        }
    }

    const marker = getVodContentMarker(episode?.vod_terms?.[0]?.price);

    const getDescription = () => {
        if (episode) {
            return episode.description || episode.tagline || series.description;
        }
        return series.description;
    };

    return (
        <PageDetailHeader
            asset={{
                title: series?.title,
                subTitle: episode ? buildEpisodeTitle(episode.episode_title, episode.season_number, episode.episode_number) : undefined,
                description: getDescription(),
                landscapeImage: series?.image_url,
                cast: series?.credits.map((credit) => ({
                    person: {
                        id: credit.person.id,
                        name: credit.person.name,
                        image_url: credit.person.image_url || '',
                    },
                    role: credit.role,
                })),
                iconList,
                captionLanguages: episode?.captionLanguages,
                originalLanguages: episode?.originalLanguages,
                buttons,
                metaData,
                contentMarker: marker ? <ContentMarker marker={marker}>{marker.value}</ContentMarker> : null,
                mobileButtons,
            }}
            overlayActive={overlayActive}
            setOverlayActive={setOverlayActive}
        />
    );
};

export default VODSeriesDetailsHeader;
